import { useNavigate } from "react-router-dom";
import { useState, useCallback, useEffect, useRef } from "react";
import Select, { components } from "react-select";
import axios from "axios";

import SpazzatoGenerale from "./Generale/Generale";
import SpazzatoDettaglio from "./Dettaglio/Dettaglio";

import FiltroCategorie from "../../components/FiltroCategorie/FiltroCategorie";
import MonthPickerModal from "../../components/MonthPickerModal/MonthPickerModal";
import { MOCKED_OPERAZIONI_SPAZZATO_CATEGORIES } from "../../mocks/MockedCategories";

import "../OperazioniHr/OperazioniHr.css";

// Componente per mostrare numero elementi selezionati
const MultiValueContainer = ({ children, selectProps, data, ...props }) => {
    const values = selectProps.value || [];
    const index = values.findIndex(v => v.value === data.value);

    if (index < 2) {
        return <components.MultiValue selectProps={selectProps} data={data} {...props}>{children}</components.MultiValue>;
    }
    if (index === 2 && values.length > 2) {
        return (
            <div className="oper-hr-select-more-badge">
                +{values.length - 2}
            </div>
        );
    }
    return null;
};

// Funzioni helper per le date
const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const monthToFirstDay = (monthValue) => {
    if (!monthValue) return '';
    const [year, month] = monthValue.split('-');
    return `${year}-${month}-01`;
};

const monthToLastDay = (monthValue) => {
    if (!monthValue) return '';
    const [year, month] = monthValue.split('-');
    const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
    return `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
};

const dateToMonth = (dateValue) => {
    if (!dateValue) return '';
    const [year, month] = dateValue.split('-');
    return `${year}-${month}`;
};

const getDefaultDateRange = () => {
    const today = new Date();
    const lastDayCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const firstDay12MonthsAgo = new Date(today.getFullYear(), today.getMonth() - 11, 1);
    return {
        from: formatDate(firstDay12MonthsAgo),
        to: formatDate(lastDayCurrentMonth),
    }
};

const defaultDates = getDefaultDateRange();


const Spazzato = () => {
    const navigate = useNavigate();

    // stati per la UI
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showFiltersPanel, setShowFiltersPanel] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [loading, setLoading] = useState(false);

    // cache per evitare reload
    const dataCache = useRef({
        filters: null,
        data: null  
    });

    // stati per i filtri
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
    const [fromDate, setFromDate] = useState(defaultDates.from);
    const [toDate, setToDate] = useState(defaultDates.to);
    const [selectedSettore, setSelectedSettore] = useState([]);
    const [selectedCommessa, setSelectedCommessa] = useState([]);
    const [selectedOperatore, setSelectedOperatore] = useState([]);
    const [selectedTipologiaVeicolo, setSelectedTipologiaVeicolo] = useState([]);
    const [selectedTarga, setSelectedTarga] = useState([]);

    // filtri applicati (passati ai componenti figli)
    const [appliedFilters, setAppliedFilters] = useState({
        fromDate: defaultDates.from,
        toDate: defaultDates.to,
        settore: [],
        commessa: [],
        operatore: [],
        tipologiaVeicolo: [],
        targa: []
    });

    // Opzioni filtri caricate dal backend
    const [filterOptions, setFilterOptions] = useState({
        tipologieVeicolo: [],
        settori: [],
        commesse: [],
        operatori: [],
        targhe: []
    });

    // Handler per la selezione del mese
    const handleFromMonthChange = (e) => {
        const monthValue = e.target.value;
        const firstDay = monthToFirstDay(monthValue);
        setFromDate(firstDay);
    };

    const handleToMonthChange = (e) => {
        const monthValue = e.target.value;
        const lastDay = monthToLastDay(monthValue);
        setToDate(lastDay);
    };

    // carico le opzioni dei filtri dal backend
    useEffect(() => {
        const fetchFilters = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API_URL}/spazzato/filters`);
                setFilterOptions(prev => ({
                    ...prev,
                    tipologieVeicolo: res.data.tipologie.map(t => ({ value: t.valore, label: t.valore })),
                    settori: res.data.settori.map(s => ({ value: s.id, label: s.valore })),
                    commesse: res.data.commesse.map(c => ({ value: c.id, label: c.valore })),
                    operatori: res.data.operatori.map(o => ({ value: o.id, label: o.valore })),
                    targhe: res.data.targhe.map(t => ({ value: t.valore, label: t.valore })),
                }))
            } catch (error) {
                console.error("Errore nel caricamento dei filtri: ", error);
            } finally {
                setLoading(false);  //loading viene disattivato sempre (amche con errori)
            }
        };
        fetchFilters();
    }, [API_URL]);

    // handler per applicare i filtri
    const handleApply = useCallback(() => {
        setAppliedFilters({
            fromDate: fromDate,
            toDate: toDate,
            settore: selectedSettore,
            commessa: selectedCommessa,
            operatore: selectedOperatore,
            tipologiaVeicolo: selectedTipologiaVeicolo,
            targa: selectedTarga
        });
    }, [fromDate, toDate, selectedSettore, selectedCommessa, selectedOperatore, selectedTipologiaVeicolo, selectedTarga]);

    // Handler per reset filtri
    const handleReset = useCallback(() => {
        setFromDate(defaultDates.from);
        setToDate(defaultDates.to);
        setSelectedSettore([]);
        setSelectedCommessa([]);
        setSelectedOperatore([]);
        setSelectedTipologiaVeicolo([]);
        setSelectedTarga([]);
        setAppliedFilters({
            fromDate: defaultDates.from,
            toDate: defaultDates.to,
            settore: [],
            commessa: [],
            operatore: [],
            tipologiaVeicolo: [],
            targa: []
        });
    }, []);

    return (
            <div className="operazioni-hr-page">
                <header className="oper-hr-header">
                    <div className="oper-hr-header-content">
                        <button className="oper-hr-back-btn" onClick={() => navigate('/reportistica')}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6"></polyline>
                            </svg>
                            Indietro
                        </button>

                        <div className="oper-hr-header-title">
                            <div className="oper-hr-title-row">
                                <h1>Spazzato</h1>
                                <button className="oper-hr-info-btn" onClick={() => setShowInfoModal(true)} title="Informazioni">
                                    <span>i</span>
                                </button>
                            </div>
                            <p className="oper-hr-subtitle">Visualizzazione dei dati sullo spazzamento</p>
                        </div>
                    </div>
                </header>

                {/* Modal Info */}
                {showInfoModal && (
                    <div className="oper-hr-modal-overlay" onClick={() => setShowInfoModal(false)}>
                        <div className="oper-hr-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="oper-hr-modal-header">
                                <div className="oper-hr-modal-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="16" x2="12" y2="12"></line>
                                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                    </svg>
                                </div>
                                <h3>Informazioni sulla pagina</h3>
                                <button className="oper-hr-modal-close" onClick={() => setShowInfoModal(false)}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>
                            <div className="oper-hr-modal-body">
                                <div className="oper-hr-modal-section">
                                    <h4>Cosa fa questa pagina?</h4>
                                    <p>
                                        Questa pagina permette di analizzare le attivita di spazzamento stradale.
                                        Puoi monitorare km spazzati, ore di lavoro e velocita di spazzamento (mt/sec)
                                        per ogni veicolo, individuando le performance e confrontando i risultati
                                        nel tempo per commessa, settore e tipologia.
                                    </p>
                                </div>
                                <div className="oper-hr-modal-section">
                                    <h4>Come utilizzarla?</h4>
                                    <ul>
                                        <li><strong>Filtri:</strong> Applica filtri per periodo (mese inizio/fine), settore, commessa, operatore, tipologia veicolo e targa</li>
                                        <li><strong>Generale:</strong> Panoramica con tabella dettaglio per targa (km spazzati, ore, mt/sec), grafico a barre per confronto tra veicoli e grafico lineare per l'andamento nel tempo con vista mensile, trimestrale o annuale. Cliccando sui grafici puoi filtrare i dati in modo dinamico</li>
                                        <li><strong>Dettaglio:</strong> Analisi avanzata con tabella parametrica configurabile per righe (settore, commessa, attivita) e colonne (anno, trimestre, mese), grafico combo per spazzato e velocita per periodo e grafico scatter per l'efficienza spazzato per commessa</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <main className="oper-hr-main">
                    {/* Bottone Mostra Filtri */}
                    <div className="oper-hr-actions">
                        <button
                            type="button"
                            className={`oper-hr-action-btn ${showFiltersPanel ? 'active' : ''}`}
                            onClick={() => setShowFiltersPanel(prev => !prev)}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                            </svg>
                            <span>{showFiltersPanel ? 'Nascondi Filtri' : 'Mostra Filtri'}</span>
                        </button>
                    </div>

                    {/* Pannello Filtri */}
                    {showFiltersPanel && (
                        <div className="oper-hr-panel">
                            <div className="oper-hr-filters-grid">
                                {/* mese inizio */}
                                <div className="oper-hr-filter-item">
                                    <label>Mese Inizio</label>
                                    <MonthPickerModal
                                        value={dateToMonth(fromDate)}
                                        onChange={handleFromMonthChange}
                                        max={dateToMonth(toDate) || undefined}
                                        label="Mese Inizio"
                                    />
                                </div>

                                {/* mese fine */}
                                <div className="oper-hr-filter-item">
                                    <label>Mese Fine</label>
                                    <MonthPickerModal
                                        value={dateToMonth(toDate)}
                                        onChange={handleToMonthChange}
                                        min={dateToMonth(fromDate) || undefined}
                                        label="Mese Fine"
                                    />
                                </div>

                                {/* Settore */}
                                <div className="oper-hr-filter-item">
                                    <label>Settore</label>
                                    <Select
                                        classNamePrefix="oper-hr-select"
                                        placeholder="Tutti"
                                        value={selectedSettore}
                                        options={filterOptions.settori}
                                        onChange={setSelectedSettore}
                                        isMulti
                                        components={{ MultiValue: MultiValueContainer }}
                                        hideSelectedOptions={false}
                                        closeMenuOnSelect={false}
                                    />
                                </div>

                                {/* Commessa */}
                                <div className="oper-hr-filter-item">
                                    <label>Commessa</label>
                                    <Select
                                        classNamePrefix="oper-hr-select"
                                        placeholder="Tutte"
                                        value={selectedCommessa}
                                        options={filterOptions.commesse}
                                        onChange={setSelectedCommessa}
                                        isMulti
                                        components={{ MultiValue: MultiValueContainer }}
                                        hideSelectedOptions={false}
                                        closeMenuOnSelect={false}
                                    />
                                </div>

                                {/* Operatore */}
                                <div className="oper-hr-filter-item">
                                    <label>Operatore</label>
                                    <Select
                                        classNamePrefix="oper-hr-select"
                                        placeholder="Tutti"
                                        value={selectedOperatore}
                                        options={filterOptions.operatori}
                                        onChange={setSelectedOperatore}
                                        isMulti
                                        components={{ MultiValue: MultiValueContainer }}
                                        hideSelectedOptions={false}
                                        closeMenuOnSelect={false}
                                    />
                                </div>

                                {/* Tipologia Veicolo */}
                                <div className="oper-hr-filter-item">
                                    <label>Tipologia Veicolo</label>
                                    <Select
                                        classNamePrefix="oper-hr-select"
                                        placeholder="Tutte"
                                        value={selectedTipologiaVeicolo}
                                        options={filterOptions.tipologieVeicolo}
                                        onChange={setSelectedTipologiaVeicolo}
                                        isMulti
                                        components={{ MultiValue: MultiValueContainer }}
                                        hideSelectedOptions={false}
                                        closeMenuOnSelect={false}
                                    />
                                </div>

                                {/* Targa */}
                                <div className="oper-hr-filter-item">
                                    <label>Targa</label>
                                    <Select
                                        classNamePrefix="oper-hr-select"
                                        placeholder="Tutte"
                                        value={selectedTarga}
                                        options={filterOptions.targhe}
                                        onChange={setSelectedTarga}
                                        isMulti
                                        components={{ MultiValue: MultiValueContainer }}
                                        hideSelectedOptions={false}
                                        closeMenuOnSelect={false}
                                    />
                                </div>

                                {/* Bottoni Applica e Reset */}
                                <div className="oper-hr-filter-actions">
                                    <button type="button" className="oper-hr-btn-primary" onClick={handleApply}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                        Applica
                                    </button>
                                    <button type="button" className="oper-hr-btn-secondary" onClick={handleReset}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                            <path d="M3 3v5h5"></path>
                                        </svg>
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tab e contenuto */}
                    <div className="oper-hr-data-section">
                        <FiltroCategorie
                            categories={MOCKED_OPERAZIONI_SPAZZATO_CATEGORIES}
                            filteredCategory={setSelectedCategory}
                            customCss={"reportistica-section"}
                        />
                        {selectedCategory === "Generale" && (
                            <SpazzatoGenerale filters={appliedFilters} loading={loading} />
                        )}
                        {selectedCategory === "Dettaglio" && (
                            <SpazzatoDettaglio filters={appliedFilters} loading={loading} />
                        )}
                    </div>
                </main>
            </div>
        );
};


export default Spazzato;